import * as React from "react";
import { Table, CustomButton, ButtonType, AlertMessage, AlertType } from "soprasteria-ui-library";
import { IPatientAttachment } from "../../../../models/IPatientAttachment";
import { dateTimeToDateString } from "../../../../helpers/DateHelper";
import { useAuth } from "../../../../App";
import { PatientAttachmentApi } from "../../../../api/PatientAttachmentApi";
import { setMessage } from "../../../../helpers/MessageHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { UploadPatientAttachmentsModal } from "../Modals/UploadPatientAttachmentsModal";

interface IPatientAttachmentsTab {
    patientId: number;
}

export const PatientAttachmentsTab: React.FunctionComponent<IPatientAttachmentsTab> = ({ patientId }) => {

    const auth = useAuth();
    const api = new PatientAttachmentApi(auth);

    const [patientAttachments, setPatientAttachments] = React.useState<IPatientAttachment[]>([]);
    const [successMessage, setSuccessMessage] = React.useState<string>("");
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [renderModal, setRenderModal] = React.useState<boolean>(false);
    const [isDeletingPatientAttachment, setIsDeletingPatientAttachment] = React.useState<boolean>(false);
    const [deletingPatientAttachmentId, setDeletingPatientAttachmentId] = React.useState<number>();

    React.useEffect(() => {
        getPatientAttachments();
    }, [patientId]);

    const getPatientAttachments = () => {
        api.getPatientAttachments(patientId, (data: IPatientAttachment[]) => {
            setPatientAttachments(data);
        }, (message: string) => {
            const newErrorMessage = setMessage(message, errorMessage);
            setErrorMessage(newErrorMessage);
        });
    }

    const downloadPatientAttachment = (patientAttachmentId: number, fileName?: string) => {
        api.downloadPatientAttachment(patientAttachmentId, fileName,
            (error) => {
                alert(error);
            });
    }

    const onSubmitPatientAttachmentsSuccess = () => {
        closeModal();
        const message = setMessage("TVE-dokument(en) har laddats upp", successMessage ?? "");
        setSuccessMessage(message);
        getPatientAttachments();
    }

    const onSubmitPatientAttachmentsError = (error: string) => {
        closeModal();
        const message = setMessage(error, errorMessage?? "");
        setErrorMessage(message);
    }

    const closeModal = () => {
        setRenderModal(false);
    }

    const deletePatientAttachment = (patientAttachmentId: number) => {
        const accepted = window.confirm("Är du säker på att du vill radera TVE-dokumentet?");
        if (accepted) {
            setIsDeletingPatientAttachment(true);
            setDeletingPatientAttachmentId(patientAttachmentId);
            api.deletePatientAttachment(patientAttachmentId, () => {
                setSuccessMessage("TVE-dokumentet har tagits bort");
                getPatientAttachments();
            }, (error) => {
                setErrorMessage(error);
            }, () => {
                setDeletingPatientAttachmentId(undefined);
                setIsDeletingPatientAttachment(false)
            });
        }
    }

    return (
        <section>
            {
                renderModal ?
                    <UploadPatientAttachmentsModal
                        patientId={patientId}
                        onSubmitSuccess={onSubmitPatientAttachmentsSuccess}
                        onSubmitError={onSubmitPatientAttachmentsError}
                        onCloseModal={closeModal}
                    />
                    : null
            }
            <AlertMessage alertType={AlertType.success}>{successMessage}</AlertMessage>
            <AlertMessage alertType={AlertType.danger} renderOnTopOfPage={true}>{errorMessage}</AlertMessage>
            <div className="panel text-right">
                <CustomButton buttonType={ButtonType.create} buttonText="Ladda upp TVE-dokument" data-toggle="modal" data-target="#patient-attachment-modal" onClick={() => setRenderModal(true)} />
            </div>
            <Table hoverableRows>
                <thead>
                    <tr>
                        <th>Datum</th>
                        <th>Uppladdat av</th>
                        <th>Fil</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {
                        patientAttachments.length > 0
                            ? patientAttachments.map((patientAttachment, key) => {
                                return (
                                    <tr key={key}>
                                        <td>{dateTimeToDateString(patientAttachment.regDate)}</td>
                                        <td>{patientAttachment.regByName}</td>
                                        <td className="clickable-row" onClick={() => downloadPatientAttachment(patientAttachment.patientAttachmentId ?? 0, patientAttachment.name)} title={`Ladda ner ${patientAttachment.name}`}><span className="col-2"><FontAwesomeIcon icon={faDownload} /></span>{patientAttachment.name}</td>
                                        <td style={{ width: "10%" }} className="delete-btn">
                                            <CustomButton
                                                buttonType={ButtonType.delete}
                                                onClick={() => deletePatientAttachment(patientAttachment.patientAttachmentId!)}
                                                buttonText=""
                                                isLoading={isDeletingPatientAttachment && deletingPatientAttachmentId === patientAttachment.patientAttachmentId} />
                                        </td>
                                    </tr>
                                )
                            })
                            : <tr><td colSpan={4}>Det finns inga TVE-dokument på denna patient</td></tr>
                    }
                </tbody>
            </Table>
        </section>
    );
}